import {
  organizationKeys,
  organizationListQueryOptions,
} from "@/lib/queries/organization";
import { trpcClient } from "@/lib/trpc";
import {
  getOrganizationType,
  isOnboardingCompleted,
  needsRoleSelection,
} from "@/lib/utils/organization";
import type { QueryClient } from "@tanstack/react-query";

/**
 * Ensures the user has at least one organization and that onboarding is
 * complete. Called before redirecting authenticated users to a `returnTo`
 * destination so that API calls at the destination (e.g. resolveByProgramId)
 * don't fail with NOT_FOUND due to missing org context.
 */
export async function ensureOrgBootstrap(
  queryClient: QueryClient,
): Promise<void> {
  const organizations = await queryClient.fetchQuery({
    ...organizationListQueryOptions(),
    staleTime: 0,
  });

  let shouldInvalidate = false;

  if (!organizations || organizations.length === 0) {
    await trpcClient.organization.createAuto.mutate();
    shouldInvalidate = true;
  } else if (
    !organizations.some(
      (o) =>
        !needsRoleSelection(o.metadata as string | null) &&
        isOnboardingCompleted(o.metadata as string | null),
    )
  ) {
    // Only skip onboarding when the user has NO fully usable org — a bare/incomplete
    // org shouldn't trigger a mutation if a usable org already exists.
    await trpcClient.organization.skipOnboarding.mutate();
    shouldInvalidate = true;
  }

  if (shouldInvalidate) {
    await queryClient.invalidateQueries({
      queryKey: organizationKeys.lists(),
    });
  }
}

type OrgSummary = { slug: string; metadata?: unknown };

/** First org the creator can use: typed as a creator org and onboarded. */
export function pickCreatorOrgSlug(
  organizations: readonly OrgSummary[] | null | undefined,
): string | null {
  const usable = (organizations ?? []).filter((o) => {
    const metadata = o.metadata as string | null;
    return !needsRoleSelection(metadata) && isOnboardingCompleted(metadata);
  });
  const creatorOrg = usable.find(
    (o) => getOrganizationType(o.metadata as string | null) === "creator",
  );
  return (creatorOrg ?? usable[0])?.slug ?? null;
}
